// src/utils/accountTypeUtils.js
//
// Shared utilities for mapping between backend account-type fields and
// user-facing labels.  The backend stores three userType values
// ('Clinician', 'Support', 'Org. Admin') plus a boolean
// isAssignableClinician flag to distinguish Clinical from Non-Clinical
// Org Admins.  The UI presents four distinct types.
//
// This module is the single source of truth for that mapping -- import
// from here rather than hardcoding the logic in individual components.

/**
 * Derives the user-facing account type label from the backend
 * userType + isAssignableClinician fields.
 *
 * - 'Org. Admin' with isAssignableClinician !== false  --> 'Org. Admin (Clinical)'
 * - 'Org. Admin' with isAssignableClinician === false  --> 'Org. Admin'
 * - Everything else                                    --> the raw userType value
 *
 * @param {{ userType: string, isAssignableClinician?: boolean }} provider
 * @returns {string}
 */
export function getUiUserType(provider) {
    if (provider.userType === 'Org. Admin') {
        return provider.isAssignableClinician === false
            ? 'Org. Admin'
            : 'Org. Admin (Clinical)';
    }
    return provider.userType;
}

/**
 * Returns true when the given backend field combination represents a
 * clinical role (i.e. the user can be assigned to patients and make
 * clinical decisions).
 *
 * Clinical roles: Clinician, Org. Admin (Clinical).
 * Non-clinical roles: Support, Org. Admin (non-clinical).
 *
 * @param {string} userType
 * @param {boolean|undefined} isAssignableClinician
 * @returns {boolean}
 */
export function isClinicalType(userType, isAssignableClinician) {
    if (userType === 'Clinician') return true;
    if (userType === 'Org. Admin' && isAssignableClinician !== false) return true;
    return false;
}

/**
 * Determines whether a transition from one account type to another is
 * "gated" -- i.e. it removes clinical capability and therefore requires
 * additional checks (current patient assignments must be zero).
 *
 * A transition is gated when moving FROM a clinical role TO a
 * non-clinical role.
 *
 * @param {string}  fromType        - Current userType
 * @param {boolean} fromAssignable  - Current isAssignableClinician
 * @param {string}  toType          - Target userType
 * @param {boolean} toAssignable    - Target isAssignableClinician
 * @returns {boolean}
 */
export function isGatedTransition(fromType, fromAssignable, toType, toAssignable) {
    const wasClinical = isClinicalType(fromType, fromAssignable);
    const willBeClinical = isClinicalType(toType, toAssignable);
    return wasClinical && !willBeClinical;
}

/**
 * Whether a transition removes admin status and therefore requires the
 * last-admin guardrail check.
 *
 * @param {string} fromType - Current userType
 * @param {string} toType   - Target userType
 * @returns {boolean}
 */
export function removesAdminStatus(fromType, toType) {
    return fromType === 'Org. Admin' && toType !== 'Org. Admin';
}

/**
 * All four account type options for the dropdown.  Each entry maps a
 * UI label to the backend field values that represent it.
 *
 * isAssignableClinician is null for types where the field should be
 * deleted from the Firestore document (Clinician and Support do not
 * use it -- the app treats a missing field as "assignable" for
 * backwards compatibility with legacy accounts).
 */
export const ACCOUNT_TYPE_OPTIONS = [
    { userType: 'Support', isAssignableClinician: null, label: 'Support' },
    { userType: 'Clinician', isAssignableClinician: null, label: 'Clinician' },
    { userType: 'Org. Admin', isAssignableClinician: true, label: 'Org. Admin (Clinical)' },
    { userType: 'Org. Admin', isAssignableClinician: false, label: 'Org. Admin' },
];

/**
 * Returns the ACCOUNT_TYPE_OPTIONS entry matching the given backend
 * fields, or undefined if no match is found.
 *
 * @param {string}  userType
 * @param {boolean} isAssignableClinician
 * @returns {object|undefined}
 */
export function getAccountTypeOption(userType, isAssignableClinician) {
    return ACCOUNT_TYPE_OPTIONS.find(opt => {
        if (opt.userType !== userType) return false;
        if (userType === 'Org. Admin') {
            return opt.isAssignableClinician === isAssignableClinician;
        }
        return true;
    });
}
